import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isTestEnv } from '../lib/testEnv';
import Icon from './Icon';

// Mobil breakpoint — theme.css dagi `@media (max-width: 768px)` bilan bir xil bo'lishi shart.
const MOBILE_QUERY = '(max-width: 768px)';

const TEXT = {
  openMenu: 'Menyuni ochish',
  closeMenu: 'Menyuni yopish',
  mainMenu: 'Asosiy menyu',
};

const NAV_ITEMS = [
  { label: 'Bosh sahifa', path: '/admin', icon: 'home', exact: true },
  { label: 'Foydalanuvchilar', path: '/admin/users', icon: 'users' },
  { label: 'Ustalar moderatsiyasi', path: '/admin/moderation', icon: 'shield' },
  { label: 'Xizmat kategoriyalari', path: '/admin/service-categories', icon: 'folder' },
  { label: "Xizmat ko'rsatuvchilar", path: '/admin/providers', icon: 'wrench' },
  { label: 'Buyurtmalar', path: '/admin/orders', icon: 'package' },
  { label: 'Nizolar', path: '/admin/disputes', icon: 'scale' },
  { label: 'Shikoyatlar', path: '/admin/user-reports', icon: 'flag' },
  { label: 'Push xabarlar', path: '/admin/notifications', icon: 'bell' },
  { label: 'Kredit hisoblari', path: '/admin/credits', icon: 'creditCard' },
  { label: 'Narx sozlamalari', path: '/admin/commission', icon: 'percent' },
  { label: 'Moliyaviy hisobot', path: '/admin/reports', icon: 'barChart' },
  // Faqat test serverda (prod'da yashirin) — ma'lumotni o'chiradi.
  { label: 'Test tozalash', path: '/admin/test-cleanup', icon: 'trash', testOnly: true },
];

function isActive(item, pathname) {
  if (item.exact) return pathname === item.path;
  return pathname === item.path || pathname.startsWith(item.path + '/');
}

export default function AdminLayout({ children }) {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // ≤768px da yon menyu yashirin; gamburger uni overlay sifatida ochadi.
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  // Ekran kengligi (resize / qurilma aylantirilishi) har qanday yo'nalishda o'zgarganda menyu
  // holati qayta tiklanadi: desktopda "ochiq" qolib, keyin torayganda o'zi ochilib qolmasin.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setMenuOpen(false);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const fullName = auth?.user
    ? [auth.user.firstName, auth.user.lastName].filter(Boolean).join(' ') || 'Admin'
    : 'Admin';

  return (
    <div style={s.shell}>
      {menuOpen && <div className="th-backdrop" style={s.backdrop} onClick={() => setMenuOpen(false)} aria-hidden="true" />}
      <aside id="admin-sidebar" className={`th-sidebar${menuOpen ? ' th-sidebar-open' : ''}`} style={s.sidebar} aria-label={TEXT.mainMenu}>
        <div style={s.brand}>
          <img src="/brand-mark.png" alt="" style={s.brandLogo} />
          <span style={s.brandName}>
            <span>Tez</span>
            <span style={s.brandAccent}>Hal</span>
          </span>
          <span style={s.brandSuffix}>Admin</span>
        </div>
        <nav style={s.nav}>
          {NAV_ITEMS.filter((item) => !item.testOnly || isTestEnv()).map((item) => {
            const active = isActive(item, location.pathname);
            return (
              <button
                key={item.path}
                onClick={() => { setMenuOpen(false); navigate(item.path); }}
                className={`th-nav-btn${active ? ' th-nav-active' : ''}`}
                style={{ ...s.navBtn, ...(active ? s.navBtnActive : {}) }}
              >
                <span style={s.navIcon}>
                  <Icon name={item.icon} size={20} />
                </span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      <div style={s.contentCol}>
        <header className="th-topbar" style={s.topbar}>
          <button
            type="button"
            className="th-burger th-ghost-btn"
            style={s.burger}
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? TEXT.closeMenu : TEXT.openMenu}
            aria-expanded={menuOpen}
            aria-controls="admin-sidebar"
          >
            <Icon name="menu" size={22} />
          </button>
          <div style={s.topbarSpacer} />
          <div style={s.userBox}>
            <span className="th-user-name" style={s.userName}>{fullName}</span>
            <button onClick={handleLogout} className="th-ghost-btn" style={s.logoutBtn}>Chiqish</button>
          </div>
        </header>
        <main className="th-main" style={s.main}>{children}</main>
      </div>
    </div>
  );
}

const s = {
  shell: {
    minHeight: '100vh',
    display: 'flex',
    background: 'var(--color-bg)',
    fontFamily: 'var(--font-family)',
  },
  sidebar: {
    width: '240px',
    flexShrink: 0,
    background: 'var(--color-dark)',
    color: 'var(--color-on-dark)',
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 0,
    height: '100vh',
    overflowY: 'auto',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '16px 20px',
    minHeight: '58px',
    boxSizing: 'border-box',
    borderBottom: '1px solid var(--color-on-dark-border)',
  },
  brandLogo: {
    // Rasm uchun eski gradient kvadrat stillari (fon, radius, markazlash) shart emas.
    width: '30px',
    height: 'auto',
    display: 'block',
  },
  brandName: { fontSize: '18px', fontWeight: '700', color: 'var(--color-on-dark)', lineHeight: 1 },
  brandAccent: { color: 'var(--color-primary)' },
  brandSuffix: { fontSize: '12px', fontWeight: '500', color: 'var(--color-text-muted)', alignSelf: 'flex-end', lineHeight: 1.2 },
  nav: { display: 'flex', flexDirection: 'column', padding: '12px 10px', gap: '4px' },
  navBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minHeight: '40px',
    boxSizing: 'border-box',
    padding: '8px 12px',
    background: 'transparent',
    color: 'var(--color-on-dark-muted)',
    border: 'none',
    borderRadius: 'var(--radius-btn)',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '500',
    textAlign: 'left',
    width: '100%',
  },
  navBtnActive: { background: 'var(--color-primary)', color: 'var(--color-on-primary)', fontWeight: '700' },
  navIcon: { width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(17,17,17,0.5)', zIndex: 290 },
  burger: {
    width: '40px',
    height: '40px',
    padding: 0,
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    color: 'var(--color-on-dark)',
    border: '1px solid var(--color-on-dark-outline)',
    borderRadius: 'var(--radius-btn)',
    cursor: 'pointer',
    flexShrink: 0,
  },
  contentCol: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  topbar: {
    height: '58px',
    boxSizing: 'border-box',
    background: 'var(--color-dark)',
    borderBottom: '1px solid var(--color-on-dark-border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  topbarSpacer: { flex: 1 },
  userBox: { display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 },
  userName: {
    fontSize: '14px', color: 'var(--color-on-dark)', fontWeight: '500',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
  },
  logoutBtn: {
    height: '40px',
    padding: '0 16px',
    background: 'transparent',
    color: 'var(--color-on-dark)',
    border: '1px solid var(--color-on-dark-outline)',
    borderRadius: 'var(--radius-btn)',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '700',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  main: { maxWidth: '1180px', width: '100%', margin: '0 auto', boxSizing: 'border-box' },
};
